import {
  ApiForecast,
  ApiForecastTimeSeries,
  RateVolumeMode,
  Source,
  TimeAggregation,
  WeatherColumns,
} from 'src/lib/api/series';

/**
 * Diff engine for proving the direct dynamical.org read path against the HF API:
 * the same query is served by both and compared point-by-point. Used by the
 * env-gated integration harness (parity.integration.test.ts); every source must
 * pass its sweep before the feature flag routes it.
 */

export type ParityTolerance = {
  /** A point passes when |a-b| ≤ absTol OR |a-b|/max(|a|,|b|) ≤ relTol. */
  relTol: number;
  absTol: number;
};

export type ParityContext = {
  source: Source;
  /**
   * Known intentional divergences (e.g. the API's GFS precipitation /3 correction
   * at leads > 120 h, which fixes a bug in the internal store that dynamical.org's
   * data doesn't have). Return true to skip the point.
   */
  allowPoint?: (column: string, validTimeMs: number, initTimeMs: number) => boolean;
};

export type ColumnParity = {
  column: string;
  comparedPoints: number;
  allowlistedPoints: number;
  /** Times present in exactly one response (epoch ms). */
  missingFromDirect: number[];
  extraInDirect: number[];
  /** Points where exactly one side is null. */
  nullMismatches: number;
  maxAbsDiff: number;
  maxRelDiff: number;
  failures: Array<{validTimeMs: number; api: number; direct: number}>;
};

export type ForecastParity = {
  initTimeMs: number;
  columns: ColumnParity[];
  validTimeRangeMatches: boolean;
};

export type ParityReport = {
  ok: boolean;
  onlyInApi: number[];
  onlyInDirect: number[];
  forecasts: ForecastParity[];
  unitMismatches: string[];
};

const HOUR_MS = 3600 * 1000;

/**
 * The tolerances the parity harness proves each source against, and the same
 * ones the production shadow-comparison counts "within tolerance" by — kept in
 * one place so telemetry can't drift from what the sweep actually validated.
 * See tolerancesFor in parity.integration.test.ts for the derivation of the
 * GEFS precipitation band (irreducible per-member masking divergence).
 */
export function productionTolerances(
  source: Source,
  rateVolumeMode: RateVolumeMode | undefined
): {[column: string]: ParityTolerance} {
  return {
    temperature: {relTol: 0.01, absTol: 0.15},
    precipitation: {
      relTol: source === Source.GEFS ? 0.15 : 0.02,
      absTol: rateVolumeMode === 'rate' ? 0.05 : 1.0,
    },
  };
}

/**
 * Extra hours of source data an aggregated point labeled T draws from past T
 * (bucket length − 1 h source step; 1D reaches 24 h so a 25-hour fall-back DST
 * day stays covered). Total over TimeAggregation so adding an aggregation forces
 * a decision here. Everything coarser than 1D is declined by the direct path, so
 * those entries are unreachable — and would allowlist every precipitation point
 * if they weren't, since their reach alone exceeds the first divided lead.
 */
const AGGREGATION_EXTRA_REACH_HOURS: {[aggregation in TimeAggregation]: number} = {
  '1H': 0,
  '6H': 5,
  '1D': 24,
  '10D': 240,
  '30D': 720,
  '90D': 2160,
  MS: 744,
  QS: 2208,
};

/**
 * The first lead the API divides GFS precipitation by 3, correcting an
 * internal-store deaccumulation bug that dynamical.org's data doesn't have. The
 * division applies past 120 h, and the GFS lead grid is hourly to 120 h then
 * 3-hourly, so 123 h is the first lead that actually gets divided.
 */
const FIRST_DIVIDED_LEAD_HOURS = 123;

/**
 * The one allowlisted divergence baked into the API: GFS precipitation past
 * lead 120 h intentionally differs from the store. An aggregated point is
 * allowlisted when any source lead in its bucket is divided — a daily bucket
 * labeled at lead 99 h reaches 123 h, the first divided lead.
 */
export function gfsPrecipAllowPoint(
  source: Source,
  timeAggregation: TimeAggregation | undefined
): ParityContext['allowPoint'] {
  if (source !== Source.GFS) {
    return undefined;
  }
  const reachMs = (timeAggregation ? AGGREGATION_EXTRA_REACH_HOURS[timeAggregation] : 0) * HOUR_MS;
  return (column, validTimeMs, initTimeMs) =>
    column === WeatherColumns.PRECIPITATION &&
    validTimeMs - initTimeMs + reachMs >= FIRST_DIVIDED_LEAD_HOURS * HOUR_MS;
}

export type ColumnDiffSummary = {maxAbsDiff: number; maxRelDiff: number; failurePoints: number};

/** A ParityReport flattened to scalar metrics for telemetry (PostHog props). */
export type ParitySummary = {
  comparedPoints: number;
  allowlistedPoints: number;
  failurePoints: number;
  nullMismatches: number;
  missingFromDirect: number;
  extraInDirect: number;
  initOnlyInApi: number;
  initOnlyInDirect: number;
  unitMismatch: boolean;
  validTimeRangeMismatch: boolean;
  maxAbsDiff: number;
  maxRelDiff: number;
  byColumn: {[column: string]: ColumnDiffSummary};
  /** First beyond-tolerance point as "column@ISO api=… direct=…", for triage. */
  firstFailure: string | null;
};

export function summarizeReport(report: ParityReport): ParitySummary {
  const summary: ParitySummary = {
    comparedPoints: 0,
    allowlistedPoints: 0,
    failurePoints: 0,
    nullMismatches: 0,
    missingFromDirect: 0,
    extraInDirect: 0,
    initOnlyInApi: report.onlyInApi.length,
    initOnlyInDirect: report.onlyInDirect.length,
    unitMismatch: report.unitMismatches.length > 0,
    validTimeRangeMismatch: false,
    maxAbsDiff: 0,
    maxRelDiff: 0,
    byColumn: {},
    firstFailure: null,
  };
  let firstFailure: {column: string; validTimeMs: number; api: number; direct: number} | null = null;

  for (const forecast of report.forecasts) {
    if (!forecast.validTimeRangeMatches) {
      summary.validTimeRangeMismatch = true;
    }
    for (const column of forecast.columns) {
      summary.comparedPoints += column.comparedPoints;
      summary.allowlistedPoints += column.allowlistedPoints;
      summary.failurePoints += column.failures.length;
      summary.nullMismatches += column.nullMismatches;
      summary.missingFromDirect += column.missingFromDirect.length;
      summary.extraInDirect += column.extraInDirect.length;
      summary.maxAbsDiff = Math.max(summary.maxAbsDiff, column.maxAbsDiff);
      summary.maxRelDiff = Math.max(summary.maxRelDiff, column.maxRelDiff);

      const existing = summary.byColumn[column.column] ?? {
        maxAbsDiff: 0,
        maxRelDiff: 0,
        failurePoints: 0,
      };
      existing.maxAbsDiff = Math.max(existing.maxAbsDiff, column.maxAbsDiff);
      existing.maxRelDiff = Math.max(existing.maxRelDiff, column.maxRelDiff);
      existing.failurePoints += column.failures.length;
      summary.byColumn[column.column] = existing;

      const failure = column.failures[0];
      if (failure && (!firstFailure || failure.validTimeMs < firstFailure.validTimeMs)) {
        firstFailure = {column: column.column, ...failure};
      }
    }
  }
  if (firstFailure) {
    summary.firstFailure =
      `${firstFailure.column}@${iso(firstFailure.validTimeMs)} ` +
      `api=${firstFailure.api} direct=${firstFailure.direct}`;
  }
  return summary;
}

function toTimeValueMap(forecast: ApiForecast, column: string): Map<number, number | null> {
  const values = forecast.data[column] as Array<number | null> | undefined;
  const map = new Map<number, number | null>();
  if (!values) {
    return map;
  }
  forecast.validTimes.forEach((time, i) => {
    map.set(new Date(time).getTime(), values[i]);
  });
  return map;
}

function compareForecast(
  api: ApiForecast,
  direct: ApiForecast,
  columns: string[],
  tolerances: {[column: string]: ParityTolerance},
  context: ParityContext
): ForecastParity {
  const initTimeMs = new Date(api.initializationTime).getTime();
  const columnResults: ColumnParity[] = [];

  for (const column of columns) {
    const apiValues = toTimeValueMap(api, column);
    const directValues = toTimeValueMap(direct, column);
    const tolerance = tolerances[column] ?? {relTol: 0.01, absTol: 0.001};

    const result: ColumnParity = {
      column,
      comparedPoints: 0,
      allowlistedPoints: 0,
      missingFromDirect: [...apiValues.keys()].filter((t) => !directValues.has(t)),
      extraInDirect: [...directValues.keys()].filter((t) => !apiValues.has(t)),
      nullMismatches: 0,
      maxAbsDiff: 0,
      maxRelDiff: 0,
      failures: [],
    };

    for (const [timeMs, apiValue] of apiValues) {
      if (!directValues.has(timeMs)) {
        continue;
      }
      const directValue = directValues.get(timeMs)!;
      if (context.allowPoint?.(column, timeMs, initTimeMs)) {
        result.allowlistedPoints += 1;
        continue;
      }
      if (apiValue === null || directValue === null) {
        if (apiValue !== directValue) {
          result.nullMismatches += 1;
        }
        continue;
      }
      result.comparedPoints += 1;
      const absDiff = Math.abs(apiValue - directValue);
      const relDiff = absDiff / Math.max(Math.abs(apiValue), Math.abs(directValue), 1e-12);
      result.maxAbsDiff = Math.max(result.maxAbsDiff, absDiff);
      result.maxRelDiff = Math.max(result.maxRelDiff, relDiff);
      if (absDiff > tolerance.absTol && relDiff > tolerance.relTol) {
        result.failures.push({validTimeMs: timeMs, api: apiValue, direct: directValue});
      }
    }
    columnResults.push(result);
  }

  return {
    initTimeMs,
    columns: columnResults,
    validTimeRangeMatches:
      new Date(api.validTimeRange[0]).getTime() === direct.validTimeRange[0].getTime() &&
      new Date(api.validTimeRange[1]).getTime() === direct.validTimeRange[1].getTime(),
  };
}

export function compareTimeSeries(
  api: ApiForecastTimeSeries,
  direct: ApiForecastTimeSeries,
  tolerances: {[column: string]: ParityTolerance},
  context: ParityContext
): ParityReport {
  const columns = Object.keys(api.variables) as Array<keyof typeof api.variables>;
  const unitMismatches = columns.filter(
    (column) => api.variables[column]?.unit !== direct.variables[column]?.unit
  );

  const directByInit = new Map(
    direct.forecasts.map((f) => [new Date(f.initializationTime).getTime(), f])
  );
  const apiByInit = new Map(
    api.forecasts.map((f) => [new Date(f.initializationTime).getTime(), f])
  );

  const forecasts: ForecastParity[] = [];
  for (const [initMs, apiForecast] of apiByInit) {
    const directForecast = directByInit.get(initMs);
    if (directForecast) {
      forecasts.push(compareForecast(apiForecast, directForecast, columns, tolerances, context));
    }
  }

  const report: ParityReport = {
    onlyInApi: [...apiByInit.keys()].filter((t) => !directByInit.has(t)),
    onlyInDirect: [...directByInit.keys()].filter((t) => !apiByInit.has(t)),
    forecasts,
    unitMismatches,
    ok: false,
  };
  report.ok =
    report.onlyInApi.length === 0 &&
    report.onlyInDirect.length === 0 &&
    unitMismatches.length === 0 &&
    forecasts.every(
      (f) =>
        f.validTimeRangeMatches &&
        f.columns.every(
          (c) =>
            c.failures.length === 0 &&
            c.nullMismatches === 0 &&
            c.missingFromDirect.length === 0 &&
            c.extraInDirect.length === 0
        )
    );
  return report;
}

const iso = (ms: number) => new Date(ms).toISOString();

export function formatReport(label: string, report: ParityReport): string {
  const lines: string[] = [`${report.ok ? 'PASS' : 'FAIL'} ${label}`];
  if (report.unitMismatches.length > 0) {
    lines.push(`  unit mismatches: ${report.unitMismatches.join(', ')}`);
  }
  if (report.onlyInApi.length > 0) {
    lines.push(`  inits only in API: ${report.onlyInApi.map(iso).join(', ')}`);
  }
  if (report.onlyInDirect.length > 0) {
    lines.push(`  inits only in direct: ${report.onlyInDirect.map(iso).join(', ')}`);
  }
  for (const forecast of report.forecasts) {
    lines.push(
      `  init ${iso(forecast.initTimeMs)}${forecast.validTimeRangeMatches ? '' : ' [validTimeRange MISMATCH]'}`
    );
    for (const column of forecast.columns) {
      const diffSummary =
        `maxAbs=${column.maxAbsDiff.toExponential(2)} maxRel=${column.maxRelDiff.toExponential(2)}` +
        ` compared=${column.comparedPoints} allowlisted=${column.allowlistedPoints}`;
      const problems: string[] = [];
      if (column.failures.length > 0) {
        problems.push(`${column.failures.length} beyond tolerance`);
      }
      if (column.nullMismatches > 0) {
        problems.push(`${column.nullMismatches} null mismatches`);
      }
      if (column.missingFromDirect.length > 0) {
        problems.push(`${column.missingFromDirect.length} times missing from direct`);
      }
      if (column.extraInDirect.length > 0) {
        problems.push(`${column.extraInDirect.length} extra times in direct`);
      }
      lines.push(
        `    ${column.column}: ${diffSummary}${problems.length ? ` — ${problems.join('; ')}` : ''}`
      );
      for (const failure of column.failures.slice(0, 5)) {
        lines.push(
          `      ${iso(failure.validTimeMs)} api=${failure.api} direct=${failure.direct}`
        );
      }
      if (column.missingFromDirect.length > 0) {
        lines.push(
          `      missing sample: ${column.missingFromDirect.slice(0, 3).map(iso).join(', ')}`
        );
      }
    }
  }
  return lines.join('\n');
}
